import type { CSSProperties } from 'react';

/**
 * EAA-1812: shimmering placeholder blocks so loading states match the shape
 * of the content they stand in for, instead of the bare "Đang tải…" text
 * used across the console today (see e.g. `AuditPage`, `HierarchyPage`,
 * `RoleMapPage`).
 *
 * Accessibility: every skeleton primitive here is `aria-hidden="true"` — a
 * screen reader gets nothing useful from N pulsing rectangles. The caller
 * owns announcing the loading state, e.g.:
 *
 * ```tsx
 * <div role="status" aria-live="polite">
 *     <span className="portal-sr-only">Đang tải…</span>
 *     {isLoading ? <SkeletonTable rows={5} columns={4} /> : <RealTable ... />}
 * </div>
 * ```
 *
 * (`.portal-sr-only` is included in this ticket's CSS block — a plain
 * visually-hidden-but-announced utility class; there wasn't one in the
 * codebase yet.) Pages that already render `<p aria-busy="true">...</p>`
 * next to their query can keep that pattern and simply swap the text node
 * for a skeleton.
 *
 * Motion: the shimmer is a `background-color` pulse (0%/100% = calm tint,
 * 50% = highlight tint), not a translating gradient sweep. That choice is
 * deliberate — the app-wide `prefers-reduced-motion: reduce` reset in
 * `global.css` forces `animation-iteration-count: 1` and a ~0 duration on
 * every animation. With `animation-fill-mode: forwards`, a finite,
 * near-instant run of this animation always settles on the 100% keyframe —
 * which is authored to be the same calm tint as 0% — so reduced motion
 * yields a static, calm block. A gradient-position sweep does not have this
 * property in general: depending on the exact end `background-position`, an
 * instant single pass can freeze on the bright highlight stop instead of a
 * resting one, which is exactly the "frozen mid-shimmer" the ticket calls
 * out to avoid.
 */

/** Accepts a bare number as px, or any CSS length string (e.g. '60%', '2.5rem'). */
type CssLength = string | number;

function toCssLength(value: CssLength): string {
    return typeof value === 'number' ? `${value}px` : value;
}

/**
 * EAA-1812: a single shimmering placeholder block. The building block for
 * `SkeletonText` and `SkeletonTable` below, and usable standalone for e.g.
 * an avatar (`<Skeleton width={32} height={32} radius="50%" />`).
 */
export function Skeleton({
    width = '100%',
    height = '1rem',
    radius = 'var(--radius-sm)',
    className = '',
}: {
    width?: CssLength;
    height?: CssLength;
    radius?: CssLength;
    className?: string;
}) {
    const style: CSSProperties = {
        width: toCssLength(width),
        height: toCssLength(height),
        borderRadius: toCssLength(radius),
    };
    return (
        <span className={`portal-skeleton ${className}`.trim()} style={style} aria-hidden="true" />
    );
}

/**
 * EAA-1812: N lines of placeholder prose, with the last line shortened so it
 * reads as text trailing off rather than a wall of identical bars.
 */
export function SkeletonText({
    lines = 3,
    lastLineWidth = '60%',
    className = '',
}: {
    lines?: number;
    lastLineWidth?: CssLength;
    className?: string;
}) {
    const safeLines = Math.max(1, Math.floor(lines));
    return (
        <span className={`portal-skeleton-text ${className}`.trim()} aria-hidden="true">
            {Array.from({ length: safeLines }, (_, index) => (
                <Skeleton
                    key={index}
                    height="0.85rem"
                    width={index === safeLines - 1 && safeLines > 1 ? lastLineWidth : '100%'}
                />
            ))}
        </span>
    );
}

/**
 * EAA-1812: a rows × columns grid of placeholder cells for table loading
 * states. Renders the *same* `.portal-admin-table` markup/class the real
 * table uses (not a lookalike with duplicated padding rules), so swapping
 * this out for the real `<table className="portal-admin-table">` cannot
 * jump the layout — the cell padding is guaranteed identical because it's
 * the same CSS rule.
 */
export function SkeletonTable({
    rows = 5,
    columns = 4,
    className = '',
}: {
    rows?: number;
    columns?: number;
    className?: string;
}) {
    const safeRows = Math.max(1, Math.floor(rows));
    const safeColumns = Math.max(1, Math.floor(columns));
    return (
        <table
            className={`portal-admin-table portal-skeleton-table ${className}`.trim()}
            aria-hidden="true"
        >
            <tbody>
                {Array.from({ length: safeRows }, (_, rowIndex) => (
                    <tr key={rowIndex}>
                        {Array.from({ length: safeColumns }, (_, colIndex) => (
                            <td key={colIndex}>
                                <Skeleton height="0.9rem" />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
